import type { CustomTableHeaderGroup, FieldConfig, ResolvedHeaderGroup } from './custom-table.type';

const fail = (message: string): never => {
    throw new Error(`CustomTableComponent grouped headers: ${message}`);
};

const assertMembershipAgainstConfig = <T>(headerGroups: CustomTableHeaderGroup[], allFields: FieldConfig<T>[]) => {
    const configuredNames = new Set(allFields.map((field) => field.name));
    const assignedTo = new Map<string, string>();

    headerGroups.forEach((group) => {
        group.fieldNames.forEach((name) => {
            if (!configuredNames.has(name)) {
                fail(`group "${group.id}" references unknown field "${name}"`);
            }
            const owner = assignedTo.get(name);
            if (owner !== undefined) {
                fail(`field "${name}" belongs to more than one group ("${owner}" and "${group.id}")`);
            }
            assignedTo.set(name, group.id);
        });
    });

    const unassigned = allFields.map((field) => field.name).filter((name) => !assignedTo.has(name));
    if (unassigned.length > 0) {
        fail(`fields not covered by any group: ${unassigned.join(', ')}`);
    }
};

/**
 * Projects the configured header groups onto the currently visible fields, returning grid
 * start/span descriptors in visible-field order. Membership is validated against every
 * configured field first, so a hidden field is distinguished from an unknown one; a group with
 * no visible leaves is dropped, and the visible leaves of a group must stay contiguous.
 */
export const resolveHeaderGroups = <T>(
    headerGroups: CustomTableHeaderGroup[],
    allFields: FieldConfig<T>[],
    visibleFields: FieldConfig<T>[]
): ResolvedHeaderGroup[] => {
    assertMembershipAgainstConfig(headerGroups, allFields);

    const visiblePositions = new Map(visibleFields.map((field, index) => [field.name, index]));

    const resolved = headerGroups.reduce<ResolvedHeaderGroup[]>((groups, group) => {
        const positions = group.fieldNames
            .map((name) => visiblePositions.get(name))
            .filter((position): position is number => position !== undefined)
            .sort((left, right) => left - right);

        if (positions.length === 0) {
            return groups;
        }

        const start = positions[0];
        const span = positions.length;
        const isContiguous = positions[span - 1] - start === span - 1;
        if (!isContiguous) {
            fail(`group "${group.id}" spans non-contiguous visible fields`);
        }

        groups.push({ id: group.id, text: group.text, start: start + 1, span });
        return groups;
    }, []);

    return resolved.sort((left, right) => left.start - right.start);
};
